import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Envelope, Lock } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { signInInputSchema, type SignInInput } from '@mybusinessmate/schema';
import { AuthShell, STAFF_BRAND } from '../../../../components/AuthShell';
import { AuthHeading } from '../../../../components/auth/auth-heading';
import { Icon } from '../../../../components/Icon';
import { Banner } from '../../../../components/ui/banner';
import { Button } from '../../../../components/ui/button';
import { Input, PasswordInput } from '../../../../components/ui/input';
import { textLinkClass } from '../../../../components/ui/text-link';
import { authClient } from '../../../../lib/api';
import { lockoutCopy, type LockoutCopy } from '../../../../lib/lockout';
import { isStaffRole, refreshSession, SESSION_NOT_KEPT_MESSAGE } from '../../../../lib/session';

export const Route = createFileRoute('/subdomain/admin/_auth/admin-login')({
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [locked, setLocked] = useState<LockoutCopy>();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInInput>({
    resolver: zodResolver(signInInputSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setLocked(undefined);
    const { data, error } = await authClient.signIn.email({ email, password });

    const mapped = mapAuthError(error);
    if (mapped) {
      switch (mapped.kind) {
        case 'invalid_credentials':
          setError('password', { message: 'That password doesn’t match. Try again or reset it.' });
          return;
        case 'account_locked':
          setLocked(lockoutCopy(mapped.retryAfterSeconds ?? 900));
          return;
        case 'rate_limited':
          setError('root', { message: 'Too many attempts. Please wait a moment and try again.' });
          return;
        case 'email_not_verified':
          setError('root', { message: 'This account isn’t active yet. Use the invite link your admin sent you.' });
          return;
        case 'suspended':
          setError('root', { message: 'This account is suspended. Please contact an admin.' });
          return;
        default:
          setError('root', { message: mapped.message });
          return;
      }
    }

    // A correct password on a two-step account is answered with a challenge, not a session.
    if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
      await navigate({ to: '/subdomain/admin/two-factor' });
      return;
    }

    // Read the new session for real before navigating, or the route guard still sees "signed out".
    const session = await refreshSession(queryClient);
    if (!session) {
      // The password was accepted but the browser never got (or won't send back) the session cookie.
      setError('root', { message: SESSION_NOT_KEPT_MESSAGE });
      return;
    }
    if (!isStaffRole(session.user.role)) {
      // A client account must not keep a session on the staff app (it would block the real staff sign-in).
      await authClient.signOut();
      await refreshSession(queryClient);
      setError('root', { message: 'This sign-in is for MyBusinessMate staff. Clients sign in at the client app.' });
      return;
    }
    await navigate({ to: '/subdomain/admin' });
  });

  return (
    <AuthShell brand={STAFF_BRAND}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-(--mbm-space-6)">
        <AuthHeading title="Staff sign in" subtitle="For the MyBusinessMate team. Use your work email." />

        {locked ? (
          <Banner tone="error" title={`Account locked for ${locked.duration}`}>
            Too many wrong passwords. Try again at {locked.until}, or reset your password now.
          </Banner>
        ) : null}
        {errors.root ? <Banner tone="error">{errors.root.message}</Banner> : null}

        <Input
          label="Email"
          type="email"
          autoComplete="username"
          leadingIcon={Envelope}
          error={errors.email?.message}
          {...register('email')}
        />

        <div className="flex flex-col gap-(--mbm-space-2)">
          <PasswordInput
            label="Password"
            autoComplete="current-password"
            error={locked ? 'Locked after 5 wrong tries.' : errors.password?.message}
            {...register('password')}
          />
          <Link to="/subdomain/admin/forgot-password" className={`${textLinkClass} self-end`}>
            Forgot password?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={isSubmitting} disabled={Boolean(locked)}>
          {locked ? `Try again at ${locked.until}` : 'Sign in'}
        </Button>

        <p className="flex items-center justify-center gap-(--mbm-space-1-5) text-center text-body-small text-text-secondary">
          <Icon icon={Lock} size="sm" />
          No account? Your admin sends you an invite.
        </p>
      </form>
    </AuthShell>
  );
}
